import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useSessionTimeout
 *
 * Tracks user inactivity and triggers a warning state before forcing logout.
 *
 * Behavior:
 * - Listens to user activity (mouse, keyboard, scroll, touch).
 * - After `idleMs` of inactivity, sets `warning = true`.
 * - After `warningMs` more without activity, calls `onTimeout`.
 * - "Activity" is shared across tabs via localStorage so working in one tab
 *   doesn't expire the session in another.
 *
 * @param {object} opts
 * @param {boolean} opts.enabled - When false, the hook is a no-op.
 * @param {number} opts.idleMs - Inactivity threshold before warning.
 * @param {number} opts.warningMs - Countdown duration before logout.
 * @param {function} opts.onTimeout - Called when warning countdown reaches 0.
 *
 * @returns {{ warning: boolean, secondsLeft: number, stayActive: () => void }}
 */
const ACTIVITY_KEY = "thales:lastActivity";
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

export function useSessionTimeout({
  enabled,
  idleMs,
  warningMs,
  onTimeout,
}) {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(warningMs / 1000));

  // Refs to avoid stale closures inside intervals/listeners
  const lastActivityRef = useRef(Date.now());
  // Timestamp at which the warning started. Used to compute a clean
  // countdown that does not depend on idle/overshoot arithmetic
  // (which broke after the browser suspended setInterval during sleep).
  const warningStartedAtRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  // Mark activity (writes to localStorage so it propagates across tabs)
  const markActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    try {
      localStorage.setItem(ACTIVITY_KEY, String(now));
    } catch {
      // localStorage may be disabled; ignore
    }
  }, []);

  // Called from the warning modal "Seguir conectado" button
  const stayActive = useCallback(() => {
    markActivity();
    warningStartedAtRef.current = null;
    setWarning(false);
    setSecondsLeft(Math.ceil(warningMs / 1000));
  }, [markActivity, warningMs]);

  useEffect(() => {
    if (!enabled) return undefined;

    // Initialize activity timestamp only on first mount (not on every warning toggle).
    // Without this guard, toggling `warning` re-runs this effect and resets
    // lastActivityRef, breaking the countdown after browser sleep.
    if (!lastActivityRef.current || Date.now() - lastActivityRef.current > idleMs + warningMs) {
      markActivity();
    }

    // Attach activity listeners
    const handleActivity = () => {
      // While in warning state, only "stayActive" should reset; ignore mere movements.
      if (!warning) {
        markActivity();
      }
    };

    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, handleActivity, { passive: true });
    });

    // Cross-tab sync: when another tab marks activity, sync local ref
    const handleStorage = (e) => {
      if (e.key === ACTIVITY_KEY && e.newValue) {
        const ts = parseInt(e.newValue, 10);
        if (!Number.isNaN(ts) && ts > lastActivityRef.current) {
          lastActivityRef.current = ts;
          if (warning) {
            warningStartedAtRef.current = null;
            setWarning(false);
            setSecondsLeft(Math.ceil(warningMs / 1000));
          }
        }
      }
    };
    window.addEventListener("storage", handleStorage);

    // Re-evaluate immediately when the tab becomes visible again
    // (the browser likely suspended our setInterval while in background).
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        evaluateNow();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    const evaluateNow = () => {
      const now = Date.now();
      const idle = now - lastActivityRef.current;

      if (warningStartedAtRef.current == null) {
        // Not yet in warning state.
        if (idle >= idleMs) {
          // Anchor the warning to the moment idle first exceeded idleMs.
          warningStartedAtRef.current = lastActivityRef.current + idleMs;
          const elapsedInWarning = now - warningStartedAtRef.current;
          const remaining = Math.max(0, Math.ceil((warningMs - elapsedInWarning) / 1000));
          setSecondsLeft(remaining);
          setWarning(true);
          if (remaining <= 0) {
            onTimeoutRef.current?.();
          }
        }
      } else {
        // Warning is active: count down based on warningStartedAtRef.
        const elapsedInWarning = now - warningStartedAtRef.current;
        const remaining = Math.max(0, Math.ceil((warningMs - elapsedInWarning) / 1000));
        setSecondsLeft(remaining);
        if (remaining <= 0) {
          onTimeoutRef.current?.();
        }
      }
    };

    // Tick every 1s
    const interval = setInterval(evaluateNow, 1000);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, handleActivity);
      });
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibility);
      clearInterval(interval);
    };
  }, [enabled, idleMs, warningMs, warning, markActivity]);

  return { warning, secondsLeft, stayActive };
}
